import type { ReactNode } from "react";
import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { scrollRootToTop } from "@/lib/scrollRootToTop";
import type { TurnoverWizardStep, TurnoverWizardStepId } from "@/lib/turnoverWizardPlan";

interface TurnoverWizardProps {
  steps: TurnoverWizardStep[];
  activeStepId: TurnoverWizardStepId;
  tDynamic: (key: string) => string;
  /** Optional: elapsed timer line when visit started */
  timerSummary?: ReactNode;
  /** Opens global unit issue report (room + issue + photo/video). */
  onReportIssue?: () => void;
  /**
   * bare = Slice 14 simple flow: no legacy "TURNOVER PROGRESS" strip
   * (steps render their own SimpleFlowProgress).
   */
  variant?: "legacy" | "bare";
  children: ReactNode;
  className?: string;
}

export function TurnoverWizard({
  steps,
  activeStepId,
  tDynamic,
  timerSummary,
  onReportIssue,
  variant = "legacy",
  children,
  className,
}: TurnoverWizardProps) {
  useEffect(() => {
    scrollRootToTop();
  }, [activeStepId]);

  if (variant === "bare") {
    return <div className={cn("min-h-[120px]", className)}>{children}</div>;
  }

  const activeIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === activeStepId)
  );
  const active = steps[activeIndex];
  const next = steps[activeIndex + 1];
  const total = Math.max(1, steps.length - 1); // exclude trailing "done" from count for bar
  const barIndex = Math.min(activeIndex, total - 1);

  const barSteps = steps.filter((s) => s.id !== "done");

  return (
    <div className={cn("space-y-4", className)}>
      <div
        className={cn(
          "turnover-wizard-strip sticky top-0 z-30 -mx-4 border-b px-4 py-3",
          "border-[color:var(--pv-line,hsl(var(--border)))]",
          "bg-[color-mix(in_srgb,var(--pv-surface,hsl(var(--background)))_94%,transparent)]",
          "backdrop-blur-md supports-[backdrop-filter]:bg-[color-mix(in_srgb,var(--pv-surface,hsl(var(--background)))_88%,transparent)]",
          "shadow-[0_1px_0_var(--pv-line-2,rgba(0,0,0,0.04))]",
          "md:static md:z-auto md:border-0 md:bg-transparent md:p-0 md:shadow-none md:backdrop-blur-none",
          "supports-[backdrop-filter]:md:bg-transparent"
        )}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))]">
            {tDynamic("Turnover progress")}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            {onReportIssue ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1 border-amber-300/80 bg-amber-50/80 px-2.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
                onClick={onReportIssue}
              >
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {tDynamic("Report")}
              </Button>
            ) : null}
            {timerSummary ? (
              <div className="flex items-center gap-1 text-xs font-mono font-semibold tabular-nums tracking-tight text-[color:var(--pv-teal,hsl(var(--primary)))]">
                {timerSummary}
              </div>
            ) : null}
          </div>
        </div>
        <div
          className="mb-3 flex items-center gap-1.5"
          role="progressbar"
          aria-valuenow={barIndex + 1}
          aria-valuemin={1}
          aria-valuemax={total}
        >
          {barSteps.map((s, i) => {
            const isCurrent = !s.complete && i === barIndex;
            return (
              <div
                key={s.id}
                className={cn(
                  "min-w-[6px] flex-1 rounded-full transition-[height,background-color,box-shadow] duration-300 ease-out",
                  s.complete && "h-1.5 bg-[color:var(--pv-teal,hsl(var(--primary)))]",
                  isCurrent &&
                    "h-2 bg-[color:var(--pv-teal,hsl(var(--primary)))]/85 shadow-[0_0_0_1px_var(--pv-teal-ring,rgba(15,168,154,0.18))]",
                  !s.complete && !isCurrent && "h-1.5 bg-[color:var(--pv-line-2,hsl(var(--muted)))]"
                )}
              />
            );
          })}
        </div>
        <div className="space-y-1">
          <h2 className="text-sm font-semibold leading-snug text-[color:var(--pv-ink,hsl(var(--foreground)))] sm:text-base">
            {active ? tDynamic(active.labelKey) : tDynamic("wizardStepDone")}
          </h2>
          {active?.detailKey && (
            <p className="text-xs leading-relaxed text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))] sm:text-sm">
              {tDynamic(active.detailKey)}
            </p>
          )}
          {next && next.id !== "done" && (
            <p className="border-t border-[color:var(--pv-divider,hsl(var(--border)))] pt-2 text-xs leading-relaxed text-[color:var(--pv-ink-2,hsl(var(--muted-foreground)))] sm:text-[13px]">
              <span className="font-semibold text-[color:var(--pv-ink,hsl(var(--foreground)))]">{tDynamic("Next")}:</span>{" "}
              {tDynamic(next.labelKey)}
            </p>
          )}
        </div>
      </div>
      <div className="min-h-[120px]">{children}</div>
    </div>
  );
}
